import { useCallback, useEffect, useState } from 'react'
import { fichasCandidatasApi } from '../api/fichasCandidatas'
import Boton from '../components/Boton'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * Fichas técnicas propuestas por el agente para los productos que entraron al
 * depósito (Martin, 23 sep 2026). Cada dato viene CON SU FUENTE: sin fuente no
 * se propone. Se aprueba (y recién ahí se publica en la ficha de la tienda) o
 * se rechaza.
 */
export default function FichasPropuestas() {
  const [data, setData] = useState({ pendientes: [], recientes: [], campos: {} })
  const [cargando, setCargando] = useState(true)
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState('')

  const cargar = useCallback(() => {
    setCargando(true)
    fichasCandidatasApi.listar()
      .then(({ data }) => setData(data))
      .catch(() => setErr('No se pudieron cargar las fichas propuestas.'))
      .finally(() => setCargando(false))
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const decidir = async (c, accion) => {
    setBusy(c.id); setErr('')
    try {
      if (accion === 'aprobar') await fichasCandidatasApi.aprobar(c.id)
      else {
        const motivo = window.prompt(`¿Por qué se rechaza la ficha de ${c.sku}? (opcional)`) ?? ''
        await fichasCandidatasApi.rechazar(c.id, motivo)
      }
      cargar()
    } catch (e) {
      setErr(e?.response?.data?.message || `No se pudo ${accion} la ficha de ${c.sku}.`)
    } finally { setBusy(null) }
  }

  if (cargando && data.pendientes.length === 0) return null

  return (
    <section style={{ marginTop: space['3xl'] }}>
      <h2 style={{ fontSize: text.title, margin: '0 0 4px' }}>Fichas técnicas propuestas</h2>
      <p style={{ fontSize: text.sec, color: colors.textSec, margin: '0 0 12px', maxWidth: '80ch' }}>
        Datos encontrados para los productos que entraron al depósito, cada uno con la página de donde salió.
        Al aprobar se publican en la ficha de la tienda; hasta entonces no los ve nadie.
      </p>

      {err && <div style={{ ...caja, background: colors.errBg, color: colors.err }}>{err}</div>}

      {data.pendientes.length === 0 ? (
        <div style={{ ...caja, background: colors.bgSoft, color: colors.textSec }}>No hay fichas esperando aprobación.</div>
      ) : (
        <div style={{ display: 'grid', gap: space.lg }}>
          {data.pendientes.map(c => (
            <article key={c.id} style={{ ...sx.card, display: 'grid', gap: space.md }}>
              <header style={{ display: 'flex', justifyContent: 'space-between', gap: space.md, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <div>
                  <span style={{ ...sx.mono, fontWeight: 600 }}>{c.sku}</span>
                  <span style={{ marginLeft: 8 }}>{c.detalle}</span>
                  {c.ean && <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta, marginLeft: 8 }}>EAN {c.ean}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ ...sx.badge, background: c.fuente_oficial ? colors.okBg : colors.warnBg, color: c.fuente_oficial ? colors.ok : colors.warn }}>
                    {c.fuente_oficial ? 'sitio de la bodega' : 'otra fuente'}
                  </span>
                  {c.confianza != null && (
                    <span style={{ fontSize: text.sec, color: colors.textSec }}>confianza {Math.round(c.confianza * 100)} %</span>
                  )}
                </div>
              </header>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: `${space.xs}px ${space.xl}px` }}>
                {Object.entries(data.campos).map(([clave, label]) => (
                  c.datos?.[clave] ? (
                    <div key={clave} style={{ fontSize: text.body, lineHeight: 1.4 }}>
                      <span style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
                      <div>{c.datos[clave]}</div>
                    </div>
                  ) : null
                ))}
              </div>

              {c.descripcion && (
                <p style={{ margin: 0, fontSize: text.body, color: colors.textSub, lineHeight: 1.5, background: colors.bgSoft, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md }}>
                  {c.descripcion}
                </p>
              )}

              <footer style={{ display: 'flex', justifyContent: 'space-between', gap: space.md, flexWrap: 'wrap', alignItems: 'center' }}>
                <a href={c.fuente_url} target="_blank" rel="noreferrer" style={{ color: colors.link, fontSize: text.sec, wordBreak: 'break-all' }}>
                  {c.fuente_url} ↗
                </a>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Boton variante="destructive" sm disabled={busy === c.id} onClick={() => decidir(c, 'rechazar')}>Rechazar</Boton>
                  <Boton variante="success" sm disabled={busy === c.id} onClick={() => decidir(c, 'aprobar')}>
                    {busy === c.id ? 'Publicando…' : '✓ Aprobar y publicar'}
                  </Boton>
                </div>
              </footer>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}

const caja = { padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, fontSize: text.body, marginBottom: space.md }
